import * as React from 'react'
import { useForm } from '@tanstack/react-form'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'
import { mutationErrorHandler } from '@/lib/error-reporting'

import type { Inquiry } from '@/gen/cortado/v1/inquiries_pb'
import { CreateQuoteRequestSchema, UpdateQuoteRequestSchema, GetInquiryRequestSchema, type QuoteEmailOverride } from '@/gen/cortado/v1/cortado_pb'
import { GetStripeAccountLinkURLRequestSchema } from '@/gen/cortado/v1/payments_pb'
import { timestampToDate } from '@/lib/inquiry-utils'
import { QuoteLineItemInputSchema, QuoteAddonOfferInputSchema, type Item, type QuoteLineItemInput } from '@/gen/cortado/v1/invoices_pb'
import { ItemUnitEnum, DiscountTypeEnum, QuoteStatusEnum, QuotePaymentScheduleAmountTypeEnum, StripeCheckoutPaymentMethodEnum } from '@/gen/cortado/v1/settings_pb'
import { useEventTypes } from '@/hooks/use-event-types'
import { CreditCard, Loader2, Send } from 'lucide-react'
import {
  appendQuoteFixedPaymentScheduleForTotal,
  formValuesToPaymentSchedules,
  getQuoteFixedPaymentScheduleTotalCents,
  paymentSchedulesToFormValues,
  rebalanceQuoteFixedPaymentSchedulesForTotal,
  type PaymentScheduleFormValue,
} from '@/lib/payment-schedule-utils'
import { FieldMaskSchema } from '@bufbuild/protobuf/wkt'
import { useQuery } from '@connectrpc/connect-query'
import { getInquiry } from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { useCreateQuote, useUpdateQuote, useQuote } from '@/stores/quotes.store'
import { useQuoteSend } from '@/hooks/use-quote-send'
import { useListItems, useListTaxRates, useListDiscounts, useListQuoteTemplates, useEnforcedTipForEventType, useGetQuotePreFill, useCurrentOrganization, useOrgTipAllocationModeDefault } from '@/stores/organization.store'
import { useStripeConnection, useGetStripeAccountLinkURL } from '@/stores/stripe.store'
import { dateToTimestamp, timestampToDateOnly } from '@/lib/date-utils'
import { dollarsToCents, formatCurrency, formatDollars } from '@/lib/currency-utils'
import { formatFullName } from '@/lib/utils'
import { itemUnitEnumToString, stringToItemUnitEnum } from '@/lib/enum-utils'
import { getDefaultAddonQuantity } from '@/lib/addon-defaults'
import { shouldSeedAddonOffer } from '@/lib/addon-offer-filters'
import { calculateLineTotalCents } from '@/lib/line-item-calculations'
import { useQuoteBuilderLiveTotals } from './use-quote-builder-live-totals'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { QuoteLineItemsTable, type QuoteLineItem } from './quote-line-items-table'
import { QuoteAddonOffersTable, type QuoteAddonOfferFormValue } from './quote-addon-offers-table'
import { haveQuoteAddonOffersChanged } from './quote-addon-offer-changes'
import { QuoteBuilderSkeleton } from './quote-builder-skeleton'
import { QuoteEventsSection, type QuoteEventFormValue } from './quote-events-section'
import { QuoteEventInputSchema } from '@/gen/cortado/v1/cortado_pb'
import {
  DiscountSelector,
  EnforcedTipInput,
  TipAllocationModeSelect,
  normalizeTipAllocationMode,
  QuickTaxSelector,
} from '@/components/shared/financial-document'
import { RichTextEditor } from '@/components/shared/rich-text-editor'
import { QuoteStatusBadge } from '@/components/shared/enum-badge'
import { useTransportationItem } from '@/stores/transportation.store'
import { getTransportationLineItem } from '@/lib/transportation-line-items'
import { defaultQuoteValidUntil, resolveQuoteExpirationRule } from '@/lib/quote-valid-until'
import {
  normalizeDepositDueDays,
  normalizeRemainderDueDays,
  recomputeBeforeEventPreviewDueDates,
} from '@/lib/quote-payment-schedule-defaults'
import { QuoteSendDialog } from './quote-send-dialog'
import { QuoteBuilderDetailsCard } from './quote-builder-details-card'
import { QuoteBuilderLineItemsCard } from './quote-builder-line-items-card'
import {
  QuoteBuilderPaymentCard,
  getQuotePaymentScheduleAmountCents,
} from './quote-builder-payment-card'
import { formatQuotePaymentScheduleDue } from '@/lib/quote-payment-schedule-defaults'
import { QuoteBuilderSummaryRail } from './quote-builder-summary-rail'
import { QuoteBuilderPreview } from './quote-builder-preview'
import { summarizeQuoteBuilderEvents } from './quote-builder-events-summary'
import {
  getQuoteBuilderChecklist,
  type QuoteBuilderSectionId,
} from './quote-builder-readiness'
import { resolveInquiryEventLocation } from './quote-event-location'
import { TransportationLineItemSection } from './transportation-line-item-section'
import {
  replacePrimaryLineItems,
  withoutTransportationLineItems,
} from '@/lib/transportation-line-items'

/**
 * Helper to safely convert discount type, filtering out UNKNOWN
 */
function sanitizeDiscountType(
  discountType: DiscountTypeEnum | undefined
): DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE | DiscountTypeEnum.DISCOUNT_TYPE_FIXED | undefined {
  if (discountType === undefined || discountType === DiscountTypeEnum.DISCOUNT_TYPE_UNKNOWN) {
    return undefined
  }
  return discountType as DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE | DiscountTypeEnum.DISCOUNT_TYPE_FIXED
}

const DEFAULT_CHECKOUT_PAYMENT_METHODS = [
  StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_CARD,
  StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_US_BANK_ACCOUNT,
]

const QUOTE_BUILDER_SECTION_DOM_IDS: Record<QuoteBuilderSectionId, string> = {
  details: 'quote-builder-details',
  items: 'quote-builder-items',
  payment: 'quote-builder-payment',
}

function formatEventDate(date: Date | undefined): string {
  if (!date) return 'Date not set'
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
}

function formatMiles(miles: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(miles)
}

/** TipTap serialises an empty document as `<p></p>`; treat that as "no description". */
function hasRichTextContent(html: string | undefined): boolean {
  if (!html) return false
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0
}

function arePaymentMethodsEqual(
  current: StripeCheckoutPaymentMethodEnum[] | undefined,
  next: StripeCheckoutPaymentMethodEnum[],
): boolean {
  const currentMethods = current ?? []
  if (currentMethods.length !== next.length) return false
  return currentMethods.every((method, index) => method === next[index])
}

type QuotePaymentScheduleValidationIssue = {
  kind: 'mixed' | 'percentage' | 'fixed'
  message: string
}

function getQuotePaymentScheduleValidationIssue(
  paymentSchedules: PaymentScheduleFormValue[],
  quoteTotalCents: number,
): QuotePaymentScheduleValidationIssue | undefined {
  if (paymentSchedules.length === 0) return undefined

  const hasPercentage = paymentSchedules.some(
    (schedule) =>
      schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_PERCENTAGE,
  )
  const hasFixed = paymentSchedules.some(
    (schedule) =>
      schedule.amountType === QuotePaymentScheduleAmountTypeEnum.QUOTE_PAYMENT_SCHEDULE_AMOUNT_TYPE_FIXED,
  )

  if (hasPercentage && hasFixed) {
    return {
      kind: 'mixed',
      message: 'Cannot mix percentage and fixed amount payment schedules.',
    }
  }

  if (hasPercentage) {
    const percentTotal = paymentSchedules.reduce((sum, schedule) => sum + (schedule.percentage || 0), 0)
    if (Math.abs(percentTotal - 100) > 0.01) {
      return {
        kind: 'percentage',
        message: `Percentages must total 100% (currently ${percentTotal.toFixed(1)}%).`,
      }
    }
    return undefined
  }

  const fixedTotalCents = getQuoteFixedPaymentScheduleTotalCents(paymentSchedules)
  if (fixedTotalCents === undefined || fixedTotalCents === quoteTotalCents) {
    return undefined
  }

  return {
    kind: 'fixed',
    message: fixedTotalCents < quoteTotalCents
      ? `Payment schedule total ($${formatCurrency(fixedTotalCents)}) is less than quote total ($${formatCurrency(quoteTotalCents)}).`
      : `Payment schedule total ($${formatCurrency(fixedTotalCents)}) exceeds quote total ($${formatCurrency(quoteTotalCents)}).`,
  }
}

function preFillLineItemToQuoteLineItem(
  input: QuoteLineItemInput,
  sortOrder: number
): QuoteLineItem {
  return {
    tempId: crypto.randomUUID(),
    sourceItemId: input.itemId || undefined,
    name: input.name || '',
    description: input.description || '',
    quantity: input.quantity,
    unitPriceCents: input.unitPriceCents ? Number(input.unitPriceCents) : 0,
    unit: input.unit ? stringToItemUnitEnum(input.unit) : ItemUnitEnum.ITEM_UNIT_PER_EVENT,
    sortOrder,
    isTaxable: input.isTaxable ?? true,
    salesTaxRateId: input.salesTaxRateId ?? undefined,
    salesTaxRateName: input.salesTaxRateName ?? undefined,
    salesTaxRate: input.salesTaxRate ?? undefined,
    salesTaxCents: input.salesTaxCents ? Number(input.salesTaxCents) : 0,
    quoteRevisionEventId: input.quoteRevisionEventId ?? undefined,
  } as QuoteLineItem
}

interface QuoteBuilderInlineProps {
  inquiry: Inquiry
  selectedQuoteId?: string
  onCancel: () => void
  onSuccess: (newQuoteId?: string) => void
  onSent?: () => void
}

/**
 * QuoteBuilderInline - Inline quote creation/viewing interface with progressive steps
 *
 * Features:
 * - Tabbed interface: Quote Details, Line Items, Review
 * - Pre-populates items from inquiry (for new quotes)
 * - Can view existing quotes (read-only initially)
 * - Real-time total calculations
 * - Drag-and-drop item reordering
 * - Inline item editing
 */
export function QuoteBuilderInline({
  inquiry,
  selectedQuoteId,
  onCancel,
  onSuccess,
  onSent,
}: QuoteBuilderInlineProps) {
  const { mutation: createMutation } = useCreateQuote()
  const { mutation: updateMutation } = useUpdateQuote()
  const { quote: existingQuote, hasLoaded: quoteHasLoaded } = useQuote(selectedQuoteId)
  const { items: availableItems, isLoading: itemsLoading } = useListItems(inquiry.organizationId)
  const { quoteTemplates, isLoading: quoteTemplatesLoading } = useListQuoteTemplates(inquiry.organizationId)
  const { taxRates } = useListTaxRates(inquiry.organizationId)
  const { discounts } = useListDiscounts(inquiry.organizationId)
  const { item: transportationItem } = useTransportationItem(inquiry.organizationId)
  const [showQuoteSendDialog, setShowQuoteSendDialog] = React.useState(false)
  const { currentOrganization } = useCurrentOrganization()
  const organizationId = currentOrganization?.id || ''
  const { isConnected: isStripeConnected, hasLoaded: stripeHasLoaded } = useStripeConnection(organizationId)
  const stripeNotConnected = stripeHasLoaded && !isStripeConnected
  const getStripeAccountLink = useGetStripeAccountLinkURL(organizationId)
  const handleConnectStripe = () => {
    if (!organizationId) return
    getStripeAccountLink.mutate(
      create(GetStripeAccountLinkURLRequestSchema, { organizationId }),
      {
        onError: (error) => {
          toast.error(error.message || 'Failed to connect Stripe')
        },
      }
    )
  }

  // Get inquiry to access event type
  const { data: inquiryData } = useQuery(
    getInquiry,
    create(GetInquiryRequestSchema, { id: inquiry.id }),
    { enabled: !!inquiry.id }
  )

  // Get enforced tip for this event type
  const orgTipAllocationMode = useOrgTipAllocationModeDefault()
  const { percentage: orgTipPercentage, hasEnforcedTip } = useEnforcedTipForEventType(
    inquiryData?.inquiry?.eventTypeKey
  )
  const { getEventTypeLabel, eventTypes } = useEventTypes()
  const isViewMode = !!selectedQuoteId
  const isCreateMode = !selectedQuoteId

  const [isEditing, setIsEditing] = React.useState(false)
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false)
  // "Apply Sales Tax to All Items" selection, lifted so it can persist and
  // auto-apply to line items added after the initial bulk apply. Empty string
  // means the auto-apply is disengaged (matches QuickTaxSelector's own ZERO_UUID -> '' convention).
  const [autoApplyTaxRateId, setAutoApplyTaxRateId] = React.useState('')
  const [discardDialogOpen, setDiscardDialogOpen] = React.useState(false)
  const [selectedQuoteTemplateId, setSelectedQuoteTemplateId] = React.useState('')
  const [hasInitializedTemplateSelection, setHasInitializedTemplateSelection] = React.useState(false)
  const [appliedTemplateSelectionKey, setAppliedTemplateSelectionKey] = React.useState<string | null>(null)
  const validUntilManuallyEditedRef = React.useRef(false)
  const requiresQuestionnaireManuallyEditedRef = React.useRef(false)
  const inquiryEventType = inquiryData?.inquiry?.eventTypeKey ?? inquiry.eventTypeKey
  const orgDepositDueDays = normalizeDepositDueDays(currentOrganization?.defaultDepositDueDaysAfterAcceptance)
  const orgRemainderDueDays = normalizeRemainderDueDays(currentOrganization?.defaultRemainderDueDaysBeforeEvent)
  const orgQuoteExpirationRule = React.useMemo(
    () => resolveQuoteExpirationRule(currentOrganization, inquiryEventType, eventTypes),
    [currentOrganization, inquiryEventType, eventTypes],
  )
  const computeDefaultValidUntil = React.useCallback(
    (eventDates: Array<Date | undefined | null>) =>
      defaultQuoteValidUntil(new Date(), orgQuoteExpirationRule, eventDates),
    [orgQuoteExpirationRule],
  )
  const isAccepted = isViewMode && existingQuote?.status === QuoteStatusEnum.QUOTE_STATUS_ACCEPTED
  const isReadOnly = isViewMode && (!isEditing || isAccepted)
  const quoteContact = inquiryData?.inquiry?.contact ?? inquiry.contact
  const recipientName = formatFullName(quoteContact?.firstName, quoteContact?.lastName, 'Unknown contact')
  const recipientEmail = quoteContact?.email
  const recipientLabel = `for ${recipientName}`

  const quoteStatus = existingQuote?.status
  const isDraft = quoteStatus === QuoteStatusEnum.QUOTE_STATUS_DRAFT
  const { sendQuote, isSending: isSendingQuote } = useQuoteSend({ isDraft, onSuccess: onSent })
  const canSend =
    isViewMode &&
    !isEditing &&
    (isDraft ||
      quoteStatus === QuoteStatusEnum.QUOTE_STATUS_SENT ||
      quoteStatus === QuoteStatusEnum.QUOTE_STATUS_VIEWED)

  const focusSection = React.useCallback((section: QuoteBuilderSectionId) => {
    window.requestAnimationFrame(() => {
      const card = document.getElementById(QUOTE_BUILDER_SECTION_DOM_IDS[section])
      card?.focus()
      card?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      })
    })
  }, [])

  const handleSendQuote = async (input: {
    additionalRecipientEmails: string[]
    emailOverride: QuoteEmailOverride
  }) => {
    if (!selectedQuoteId) return false
    return sendQuote({ quoteId: selectedQuoteId, ...input })
  }

  const handleOpenSendDialog = () => {
    if (!recipientEmail?.trim()) {
      toast.error('Add an email address to the quote contact before sending.')
      return
    }
    setShowQuoteSendDialog(true)
  }

  const sortedActiveQuoteTemplates = React.useMemo(() => {
    const eventType = inquiryEventType || undefined

    return quoteTemplates
      .filter((template) => template.isActive)
      .slice()
      .sort((a, b) => {
        const aRank = eventType && a.eventTypeKey === eventType ? 0 : !a.eventTypeKey ? 1 : 2
        const bRank = eventType && b.eventTypeKey === eventType ? 0 : !b.eventTypeKey ? 1 : 2
        if (aRank !== bRank) return aRank - bRank
        return a.title.localeCompare(b.title)
      })
  }, [quoteTemplates, inquiryEventType])

  React.useEffect(() => {
    setSelectedQuoteTemplateId('')
    setHasInitializedTemplateSelection(false)
    setAppliedTemplateSelectionKey(null)
  }, [inquiry.id, isCreateMode])

  React.useEffect(() => {
    if (!isCreateMode || hasInitializedTemplateSelection || quoteTemplatesLoading) return

    const eventType = inquiryEventType || undefined
    const matchingTemplate = eventType
      ? sortedActiveQuoteTemplates.find((template) => template.eventTypeKey === eventType)
      : undefined

    setSelectedQuoteTemplateId(matchingTemplate?.id ?? '')
    setHasInitializedTemplateSelection(true)
  }, [
    hasInitializedTemplateSelection,
    inquiryEventType,
    isCreateMode,
    quoteTemplatesLoading,
    sortedActiveQuoteTemplates,
  ])

  React.useEffect(() => {
    setAppliedTemplateSelectionKey(null)
  }, [selectedQuoteTemplateId])

  const shouldPreFill = isCreateMode && hasInitializedTemplateSelection && !!selectedQuoteTemplateId
  const {
    quoteTitle: preFillQuoteTitle,
    quoteDescription: preFillQuoteDescription,
    eventGroups: preFillEventGroups,
    generalItems: preFillGeneralItems,
    quoteEvents: preFillQuoteEvents,
    isLoading: preFillLoading,
  } = useGetQuotePreFill(
    shouldPreFill ? inquiry.id : '',
    selectedQuoteTemplateId || undefined
  )

  // Pre-populate items from inquiry (for new quotes) or from existing quote
  const initialItems = React.useMemo(() => {
    if (isViewMode && existingQuote?.lineItems) {
      return existingQuote.lineItems.map((item) => ({
        id: item.id,
        sourceItemId: item.sourceItemId,
        name: item.name,
        description: item.description || '',
        quantity: item.quantity,
        unitPriceCents: Number(item.unitPriceCents),
        unit: item.unit ? stringToItemUnitEnum(item.unit) : ItemUnitEnum.ITEM_UNIT_PER_EVENT,
        sortOrder: item.sortOrder,
        lineTotalCents: item.lineTotalCents ? Number(item.lineTotalCents) : undefined,
        isTaxable: item.isTaxable ?? true,
        salesTaxRateId: item.salesTaxRateId ?? undefined,
        salesTaxRateName: item.salesTaxRateName ?? undefined,
        salesTaxRate: item.salesTaxRate ?? undefined,
        salesTaxCents: item.salesTaxCents ? Number(item.salesTaxCents) : 0,
      } as QuoteLineItem))
    }

    if (isCreateMode) {
      let sortOrder = 0

      if (!selectedQuoteTemplateId && hasInitializedTemplateSelection) {
        const seenItemIds = new Set<string>()
        const inquiryItemIds = new Set(inquiry.itemIds)
        const nextItems: QuoteLineItem[] = []

        const addCatalogItem = (item: Item) => {
          if (
            !item.id
            || seenItemIds.has(item.id)
            || item.unit === ItemUnitEnum.ITEM_UNIT_PER_MILE
          ) return
          seenItemIds.add(item.id)
          nextItems.push({
            tempId: crypto.randomUUID(),
            sourceItemId: item.id,
            name: item.title,
            description: item.description || '',
            quantity: 1,
            unitPriceCents: dollarsToCents(item.price),
            unit: item.unit || ItemUnitEnum.ITEM_UNIT_PER_EVENT,
            sortOrder: sortOrder++,
            isTaxable: true,
            salesTaxCents: 0,
          } as QuoteLineItem)
        }

        availableItems.filter((item) => item.alwaysInclude).forEach(addCatalogItem)
        availableItems.filter((item) => inquiryItemIds.has(item.id)).forEach(addCatalogItem)

        return nextItems
      }

      // Event-grouped line items from pre-fill
      const eventItems = preFillEventGroups.flatMap((group) =>
        group.lineItems.map((li) => preFillLineItemToQuoteLineItem(li, sortOrder++))
      )

      // General items (inquiry-selected + always-include) from pre-fill
      const generalItems = preFillGeneralItems.map((li) =>
        preFillLineItemToQuoteLineItem(li, sortOrder++)
      )

      // Transportation is enabled separately and must be calculated before a
      // mileage line is created. Do not turn a catalog/template default into a
      // one-mile charge.
      return withoutTransportationLineItems([...eventItems, ...generalItems])
    }

    return []
  }, [
    availableItems,
    existingQuote,
    hasInitializedTemplateSelection,
    inquiry.itemIds,
    isCreateMode,
    isViewMode,
    preFillEventGroups,
    preFillGeneralItems,
    selectedQuoteTemplateId,
  ])

  // sourceItemIds already seeded as line items, so the same catalog item
  // isn't also offered as an add-on (e.g. a lead-picked event option).
  const initialItemSourceIds = React.useMemo(
    () => new Set(initialItems.map((item) => item.sourceItemId).filter((id): id is string => !!id)),
    [initialItems]
  )

  // Events used to derive a default add-on quantity when seeding new offers
  // from the catalog in create mode. Mirrors the precedence used for the
  // `quoteEvents` form field default below (pre-fill events, then inquiry events).
  const addonDefaultQuoteEvents = React.useMemo(() => {
    if (!isCreateMode) return []
    if (preFillQuoteEvents.length > 0) return preFillQuoteEvents
    return inquiry.events ?? []
  }, [isCreateMode, preFillQuoteEvents, inquiry.events])

  // Pre-populate add-on offers from catalog items flagged as add-ons (for new quotes)
  // or from the existing quote's saved offers
  const initialAddonOffers = React.useMemo(() => {
    if (isViewMode && existingQuote?.addonOffers) {
      return existingQuote.addonOffers.map((offer) => ({
        id: offer.id,
        sourceItemId: offer.sourceItemId,
        name: offer.name,
        description: offer.description || '',
        unitPriceCents: Number(offer.unitPriceCents),
        unit: offer.unit ? stringToItemUnitEnum(offer.unit) : ItemUnitEnum.ITEM_UNIT_PER_EVENT,
        quantity: offer.quantity,
        sortOrder: offer.sortOrder,
        isTaxable: offer.isTaxable ?? false,
        salesTaxRateId: offer.salesTaxRateId ?? undefined,
        salesTaxRateName: offer.salesTaxRateName ?? undefined,
        salesTaxRate: offer.salesTaxRate ?? undefined,
      } as QuoteAddonOfferFormValue))
    }

    if (isCreateMode) {
      return availableItems
        .filter((item) => shouldSeedAddonOffer(item, initialItemSourceIds))
        .slice()
        .sort((a, b) => a.formDisplayOrder - b.formDisplayOrder)
        .map((item, index) => {
          const unit = item.unit || ItemUnitEnum.ITEM_UNIT_PER_EVENT
          return {
            tempId: crypto.randomUUID(),
            sourceItemId: item.id,
            name: item.title,
            description: item.description || '',
            unitPriceCents: dollarsToCents(item.price),
            unit,
            quantity: getDefaultAddonQuantity(unit, addonDefaultQuoteEvents),
            sortOrder: index,
            isTaxable: true,
          } as QuoteAddonOfferFormValue
        })
    }

    return []
  }, [addonDefaultQuoteEvents, availableItems, existingQuote, initialItemSourceIds, isCreateMode, isViewMode])

  // Setup TipTap editor for description field
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Placeholder.configure({
        placeholder: 'Enter quote description…',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[100px] px-3 py-2 text-sm',
      },
    },
  })

  const mapPreFillQuoteEventsToFormValues = React.useCallback(() => (
    preFillQuoteEvents.map((qe) => ({
      tempId: crypto.randomUUID(),
      eventDate: qe.eventDate ? timestampToDate(qe.eventDate) : undefined,
      eventLength: qe.eventLength,
      eventGuests: qe.eventGuests,
      eventTimezone: qe.eventTimezone,
      eventLocation: qe.eventLocation,
      baristaCount: qe.baristaCount,
      cartCount: qe.cartCount,
    } as QuoteEventFormValue))
  ), [preFillQuoteEvents])

  const mapInquiryEventsToFormValues = React.useCallback(() => (
    inquiry.events && inquiry.events.length > 0
      ? inquiry.events.map((ie) => ({
          tempId: crypto.randomUUID(),
          eventDate: ie.eventDate ? timestampToDate(ie.eventDate) : undefined,
          eventLength: ie.eventLength,
          eventGuests: ie.eventGuests,
          eventTimezone: ie.eventTimezone,
          eventLocation: resolveInquiryEventLocation(ie.eventLocation, inquiry.eventLocation),
          baristaCount: ie.baristaCount,
          cartCount: ie.cartCount,
        } as QuoteEventFormValue))
      : [] as QuoteEventFormValue[]
  ), [inquiry.eventLocation, inquiry.events])

  // Default quote events for the form. These are memoized because TanStack
  // Form re-runs `formApi.update(opts)` in a layout effect after every render
  // and deep-compares `defaultValues`; while the form is untouched, a
  // difference resets the store and re-renders every subscriber. Each event
  // carries a `crypto.randomUUID()` tempId, so building this array inline in
  // `defaultValues` made every render look like new defaults and looped React
  // to its update-depth limit (#185) after Create Quote (CORTADO-FRONTEND-2A).
  const existingQuoteEvents = React.useMemo(() => (
    existingQuote?.quoteEvents && existingQuote.quoteEvents.length > 0
      ? existingQuote.quoteEvents.map((qe) => ({
          tempId: crypto.randomUUID(),
          id: qe.id,
          eventDate: qe.eventDate ? timestampToDate(qe.eventDate) : undefined,
          eventLength: qe.eventLength,
          eventGuests: qe.eventGuests,
          eventTimezone: qe.eventTimezone,
          eventLocation: qe.eventLocation,
          baristaCount: qe.baristaCount,
          cartCount: qe.cartCount,
        } as QuoteEventFormValue))
      : [] as QuoteEventFormValue[]
  ), [existingQuote?.quoteEvents])

  const initialQuoteEvents = React.useMemo(() => {
    if (isCreateMode && preFillQuoteEvents.length > 0) {
      return mapPreFillQuoteEventsToFormValues()
    }
    if (isCreateMode) {
      return mapInquiryEventsToFormValues()
    }
    return [] as QuoteEventFormValue[]
  }, [
    isCreateMode,
    mapInquiryEventsToFormValues,
    mapPreFillQuoteEventsToFormValues,
    preFillQuoteEvents.length,
  ])

  const defaultValidUntil = React.useMemo(
    () => computeDefaultValidUntil(initialQuoteEvents.map((event) => event.eventDate)),
    [computeDefaultValidUntil, initialQuoteEvents],
  )

  // Setup TanStack Form
  const form = useForm({
    defaultValues: {
      title: existingQuote?.title || '',
      description: existingQuote?.description || '',
      validUntil: existingQuote?.validUntil
        ? new Date(
            typeof existingQuote.validUntil.seconds === 'bigint'
              ? Number(existingQuote.validUntil.seconds) * 1000
              : existingQuote.validUntil.seconds * 1000
          )
        : defaultValidUntil,
      // Initialize discount fields - preserve all fields independently
      discountId: existingQuote?.discountId || undefined,
      discountName: existingQuote?.discountName || undefined,
      discountType: sanitizeDiscountType(existingQuote?.discountType),
      discountValue: existingQuote?.discountValue || undefined,
      enforcedTipEnabled: existingQuote?.enforcedTipEnabled || false,
      enforcedTipPercentage: existingQuote?.enforcedTipPercentage || undefined,
      tipAllocationMode: existingQuote
        ? normalizeTipAllocationMode(existingQuote.tipAllocationMode)
        : orgTipAllocationMode,
      requiresQuestionnaire: existingQuote?.requiresQuestionnaire ?? inquiry.requiresQuestionnaire ?? false,
      // Quote events - existing quote's events, else prefill from the pre-fill
      // response or inquiry events when creating a new quote. Must stay
      // referentially stable across renders (see the memos above).
      quoteEvents: existingQuoteEvents.length > 0 ? existingQuoteEvents : initialQuoteEvents,
      items: initialItems.map(item => ({
        ...item,
        isTaxable: item.isTaxable ?? true,
        salesTaxRateId: item.salesTaxRateId ?? undefined,
        salesTaxRateName: item.salesTaxRateName ?? undefined,
        salesTaxRate: item.salesTaxRate ?? undefined,
        salesTaxCents: item.salesTaxCents ?? 0,
      })),
      addonOffers: initialAddonOffers,
      paymentSchedules: existingQuote?.paymentSchedules
        ? paymentSchedulesToFormValues(existingQuote.paymentSchedules)
        : [],
      useCustomPaymentMethods: (existingQuote?.checkoutPaymentMethods?.length ?? 0) > 0,
      checkoutPaymentMethods: existingQuote?.checkoutPaymentMethods?.length
        ? [...existingQuote.checkoutPaymentMethods]
        : [...DEFAULT_CHECKOUT_PAYMENT_METHODS],
    },
    onSubmit: async ({ value }) => {
      // Title is required by the backend for both create and update
      if (!value.title?.trim()) {
        focusSection('details')
        toast.error('Add a quote title', { description: 'Enter a title before creating this quote.' })
        return
      }

      if (value.items.length === 0) {
        focusSection('items')
        toast.error('Add a line item', { description: 'A quote needs at least 1 line item.' })
        return
      }

      // Validate payment schedules before submission
      const paymentScheduleValidationIssue = getQuotePaymentScheduleValidationIssue(
        value.paymentSchedules,
        totalCents,
      )
      if (paymentScheduleValidationIssue) {
        focusSection('payment')
        toast.error(
          paymentScheduleValidationIssue.kind === 'fixed' && isViewMode
            ? 'Choose a payment schedule update'
            : 'Invalid payment schedule',
          { description: paymentScheduleValidationIssue.message },
        )
        return
      }

      if (isViewMode && selectedQuoteId) {
        // Update existing quote
        try {
          // Get HTML content from editor
          const htmlContent = editor?.getHTML() || ''

          // Build field mask paths based on what fields have actually changed
          const paths: string[] = []

          // Check if title changed
          if (value.title !== existingQuote?.title) {
            paths.push('title')
          }

          // Check if description changed
          if (htmlContent !== existingQuote?.description) {
            paths.push('description')
          }

          // Check if validUntil changed (compare date portion only to avoid timezone issues)
          const existingValidUntil = existingQuote?.validUntil
            ? new Date(
                typeof existingQuote.validUntil.seconds === 'bigint'
                  ? Number(existingQuote.validUntil.seconds) * 1000
                  : existingQuote.validUntil.seconds * 1000
              ).toISOString().split('T')[0]
            : null
          const newValidUntil = value.validUntil?.toISOString().split('T')[0] || null
          if (existingValidUntil !== newValidUntil) {
            paths.push('valid_until')
          }

          // Check if line items have changed
          const itemsChanged = (() => {
            const original = existingQuote?.lineItems
            const updated = value.items

            if (!original) return updated.length > 0
            if (original.length !== updated.length) return true

            // Compare each item's relevant properties
            return original.some((origItem, index) => {
              const updatedItem = updated[index]
              if (!updatedItem) return true

              // Convert origItem.unit (string) to enum for comparison
              const origUnitEnum = origItem.unit ? stringToItemUnitEnum(origItem.unit) : ItemUnitEnum.ITEM_UNIT_PER_EVENT

              return (
                origItem.sourceItemId !== updatedItem.sourceItemId ||
                origItem.quantity !== updatedItem.quantity ||
                Number(origItem.unitPriceCents) !== updatedItem.unitPriceCents ||
                origItem.name !== updatedItem.name ||
                origItem.description !== updatedItem.description ||
                origUnitEnum !== updatedItem.unit ||
                origItem.sortOrder !== updatedItem.sortOrder ||
                // Tax field comparisons
                origItem.isTaxable !== updatedItem.isTaxable ||
                origItem.salesTaxRateId !== updatedItem.salesTaxRateId ||
                origItem.salesTaxRate !== updatedItem.salesTaxRate
              )
            })
          })()

          if (itemsChanged) {
            paths.push('line_items')
          }

          // Check if discount has been changed from initial state
          const hasDiscountChanged =
            value.discountId !== existingQuote?.discountId ||
            value.discountName !== existingQuote?.discountName ||
            value.discountType !== existingQuote?.discountType ||
            value.discountValue !== existingQuote?.discountValue

          // Add discount fields to update mask if discount has changed
          if (hasDiscountChanged) {
            paths.push('discount_id', 'discount_name', 'discount_type', 'discount_value')
          }

          // Check if enforced tip has changed
          const hasEnforcedTipChanged =
            value.enforcedTipEnabled !== existingQuote?.enforcedTipEnabled ||
            value.enforcedTipPercentage !== existingQuote?.enforcedTipPercentage

          if (hasEnforcedTipChanged) {
            paths.push('enforced_tip_enabled', 'enforced_tip_percentage')
          }

          // The tip collection mode is its own field, independent of the tip amount
          if (
            normalizeTipAllocationMode(value.tipAllocationMode) !==
            normalizeTipAllocationMode(existingQuote?.tipAllocationMode)
          ) {
            paths.push('tip_allocation_mode')
          }

          // Check if quote events have changed
          const hasQuoteEventsChanged = (() => {
            const original = existingQuote?.quoteEvents || []
            const updated = value.quoteEvents || []

            if (original.length !== updated.length) return true

            return original.some((origEvent, index) => {
              const updatedEvent = updated[index]
              if (!updatedEvent) return true

              const origDate = origEvent.eventDate
                ? new Date(
                    typeof origEvent.eventDate.seconds === 'bigint'
                      ? Number(origEvent.eventDate.seconds) * 1000
                      : origEvent.eventDate.seconds * 1000
                  ).getTime()
                : null
              const updatedDate = updatedEvent.eventDate?.getTime() || null

              return (
                origDate !== updatedDate ||
                origEvent.eventLength !== updatedEvent.eventLength ||
                origEvent.eventGuests !== updatedEvent.eventGuests ||
                (origEvent.eventLocation || '') !== (updatedEvent.eventLocation || '') ||
                (origEvent.eventTimezone || '') !== (updatedEvent.eventTimezone || '') ||
                (origEvent.baristaCount ?? undefined) !== (updatedEvent.baristaCount ?? undefined) ||
                (origEvent.cartCount ?? undefined) !== (updatedEvent.cartCount ?? undefined)
              )
            })
          })()

          if (hasQuoteEventsChanged) {
            paths.push('quote_events')
          }

          // Check if payment schedules have changed
          const hasPaymentSchedulesChanged = (() => {
            const original = existingQuote?.paymentSchedules || []
            const updated = value.paymentSchedules || []

            if (original.length !== updated.length) return true

            return original.some((origSchedule, index) => {
              const updatedSchedule = updated[index]
              if (!updatedSchedule) return true

              // Compare relevant properties
              const origDueDate = timestampToDateOnly(origSchedule.dueDate)?.getTime() ?? 0
              const updatedDueDate = updatedSchedule.dueDate
                ? updatedSchedule.dueDate.getTime()
                : 0

              return (
                origSchedule.sequenceNumber !== updatedSchedule.sequenceNumber ||
                origSchedule.description !== updatedSchedule.description ||
                origDueDate !== updatedDueDate ||
                origSchedule.amountType !== updatedSchedule.amountType ||
                origSchedule.percentage !== updatedSchedule.percentage ||
                Number(origSchedule.amountCents || 0n) !== (updatedSchedule.amountCents || 0)
              )
            })
          })()

          if (hasPaymentSchedulesChanged) {
            paths.push('payment_schedules')
          }

          // Check if add-on offers have changed
          const hasAddonOffersChanged = haveQuoteAddonOffersChanged(
            existingQuote?.addonOffers || [],
            value.addonOffers || [],
          )

          if (hasAddonOffersChanged) {
            paths.push('addon_offers')
          }

          // Build line items array from form items if changed
          const lineItems = itemsChanged ? value.items.map((item) => {
            const isCustomItem = !item.sourceItemId

            return create(QuoteLineItemInputSchema, {
              itemId: isCustomItem ? undefined : item.sourceItemId,
              quantity: item.quantity,
              unitPriceCents: BigInt(item.unitPriceCents),
              // Always send name, description, and unit so overrides are preserved for catalog items too
              name: item.name,
              description: item.description,
              unit: itemUnitEnumToString(item.unit),
              // Per-line-item tax fields
              isTaxable: item.isTaxable,
              salesTaxRateId: item.salesTaxRateId,
              salesTaxRateName: item.salesTaxRateName,
              salesTaxRate: item.salesTaxRate,
              salesTaxCents: BigInt(item.salesTaxCents || 0),
              quoteRevisionEventId: item.quoteRevisionEventId,
            })
          }) : []

          // Build quote events array for the request
          const quoteEventsInput = hasQuoteEventsChanged
            ? value.quoteEvents.map((qe) =>
                create(QuoteEventInputSchema, {
                  eventDate: qe.eventDate ? dateToTimestamp(qe.eventDate) : undefined,
                  eventLength: qe.eventLength,
                  eventGuests: qe.eventGuests,
                  eventTimezone: qe.eventTimezone,
                  eventLocation: qe.eventLocation,
                  baristaCount: qe.baristaCount,
                  cartCount: qe.cartCount,
                })
              )
            : []

          // Build add-on offers array for the request if changed
          const addonOffersInput = hasAddonOffersChanged
            ? value.addonOffers.map((offer) =>
                create(QuoteAddonOfferInputSchema, {
                  id: offer.id,
                  itemId: offer.sourceItemId,
                  name: offer.name,
                  description: offer.description,
                  unitPriceCents: BigInt(offer.unitPriceCents),
                  unit: itemUnitEnumToString(offer.unit),
                  quantity: offer.quantity,
                  sortOrder: offer.sortOrder,
                  isTaxable: offer.isTaxable,
                  salesTaxRateId: offer.salesTaxRateId,
                  salesTaxRateName: offer.salesTaxRateName,
                  salesTaxRate: offer.salesTaxRate,
                })
              )
            : []

          const nextCheckoutPaymentMethods = value.useCustomPaymentMethods
            ? value.checkoutPaymentMethods
            : []
          if (!arePaymentMethodsEqual(existingQuote?.checkoutPaymentMethods, nextCheckoutPaymentMethods)) {
            paths.push('checkout_payment_methods')
          }

          if (value.requiresQuestionnaire !== (existingQuote?.requiresQuestionnaire ?? false)) {
            paths.push('requires_questionnaire')
          }

          const request = create(UpdateQuoteRequestSchema, {
            id: selectedQuoteId,
            title: value.title || undefined,
            description: htmlContent || undefined,
            validUntil: value.validUntil ? dateToTimestamp(value.validUntil) : undefined,
            discountId: value.discountId || undefined,
            discountName: value.discountName || undefined,
            discountType: value.discountType || undefined,
            discountValue: value.discountValue || undefined,
            enforcedTipEnabled: value.enforcedTipEnabled,
            enforcedTipPercentage: value.enforcedTipPercentage,
            tipAllocationMode: normalizeTipAllocationMode(value.tipAllocationMode),
            requiresQuestionnaire: value.requiresQuestionnaire,
            lineItems: lineItems,
            addonOffers: addonOffersInput,
            paymentSchedules: hasPaymentSchedulesChanged
              ? formValuesToPaymentSchedules(value.paymentSchedules)
              : [],
            quoteEvents: quoteEventsInput,
            checkoutPaymentMethods: nextCheckoutPaymentMethods,
            updateMask: create(FieldMaskSchema, {
              paths,
            }),
          })

          await updateMutation.mutateAsync(request)

          toast.success('Quote updated successfully')
          setIsEditing(false)
          setHasUnsavedChanges(false)
        } catch (error) {
          console.error('Failed to update quote:', error)
          captureError(error instanceof Error ? error : new Error(String(error)), { context: 'quote-builder-update' })
          mutationErrorHandler({ feature: 'quote_update' })(error)
        }
        return
      }

      // Create new quote
      try {
        // Get HTML content from editor
        const htmlContent = editor?.getHTML() || ''

        // Discount type is already an enum from form state
        const discountTypeEnum = value.discountType

        // Build line items array from form items
        const lineItems = value.items.map((item) => {
          const isCustomItem = !item.sourceItemId

          return create(QuoteLineItemInputSchema, {
            itemId: isCustomItem ? undefined : item.sourceItemId,
            quantity: item.quantity,
            unitPriceCents: BigInt(item.unitPriceCents),
            // Always send name, description, and unit so overrides are preserved for catalog items too
            name: item.name,
            description: item.description,
            unit: itemUnitEnumToString(item.unit),
            // Per-line-item tax fields
            isTaxable: item.isTaxable,
            salesTaxRateId: item.salesTaxRateId,
            salesTaxRateName: item.salesTaxRateName,
            salesTaxRate: item.salesTaxRate,
            salesTaxCents: BigInt(item.salesTaxCents || 0),
            quoteRevisionEventId: item.quoteRevisionEventId,
          })
        })

        // Build quote events array for the request
        const quoteEventsInput = value.quoteEvents.map((qe) =>
          create(QuoteEventInputSchema, {
            eventDate: qe.eventDate ? dateToTimestamp(qe.eventDate) : undefined,
            eventLength: qe.eventLength,
            eventGuests: qe.eventGuests,
            eventTimezone: qe.eventTimezone,
            eventLocation: qe.eventLocation,
            baristaCount: qe.baristaCount,
            cartCount: qe.cartCount,
          })
        )

        // Build add-on offers array for the request
        const addonOffersInput = value.addonOffers.map((offer) =>
          create(QuoteAddonOfferInputSchema, {
            id: offer.id,
            itemId: offer.sourceItemId,
            name: offer.name,
            description: offer.description,
            unitPriceCents: BigInt(offer.unitPriceCents),
            unit: itemUnitEnumToString(offer.unit),
            quantity: offer.quantity,
            sortOrder: offer.sortOrder,
            isTaxable: offer.isTaxable,
            salesTaxRateId: offer.salesTaxRateId,
            salesTaxRateName: offer.salesTaxRateName,
            salesTaxRate: offer.salesTaxRate,
          })
        )

        const request = create(CreateQuoteRequestSchema, {
          inquiryId: inquiry.id,
          title: value.title || undefined,
          description: htmlContent || undefined,
          validUntil: value.validUntil ? dateToTimestamp(value.validUntil) : undefined,
          lineItems: lineItems,
          addonOffers: addonOffersInput,
          discountId: value.discountId || undefined,
          discountName: value.discountName || undefined,
          discountType: discountTypeEnum,
          discountValue: value.discountValue || undefined,
          enforcedTipEnabled: value.enforcedTipEnabled,
          enforcedTipPercentage: value.enforcedTipPercentage,
          tipAllocationMode: normalizeTipAllocationMode(value.tipAllocationMode),
          requiresQuestionnaire: requiresQuestionnaireManuallyEditedRef.current
            ? value.requiresQuestionnaire
            : undefined,
          paymentSchedules: formValuesToPaymentSchedules(value.paymentSchedules),
          quoteEvents: quoteEventsInput,
          checkoutPaymentMethods: value.useCustomPaymentMethods ? value.checkoutPaymentMethods : [],
        })

        const response = await createMutation.mutateAsync(request)

        toast.success('Quote created successfully')
        onSuccess(response.quote?.id)
      } catch (error) {
        console.error('Failed to create quote:', error)
        captureError(error instanceof Error ? error : new Error(String(error)), { context: 'quote-builder-create' })
        mutationErrorHandler({ feature: 'quote_create' })(error)
      }
    },
  })

  // Update form when editor content changes
  React.useEffect(() => {
    if (editor) {
      const handleUpdate = () => {
        form.setFieldValue('description', editor.getHTML())
        if (isEditing) {
          setHasUnsavedChanges(true)
        }
      }
      editor.on('update', handleUpdate)
      return () => {
        editor.off('update', handleUpdate)
      }
    }
  }, [editor, form, isEditing])

  // Set editor content when quote is loaded or reset
  React.useEffect(() => {
    if (editor) {
      if (isViewMode && existingQuote) {
        // Set editor content from existing quote HTML
        editor.commands.setContent(existingQuote.description || '')
      } else if (isCreateMode) {
        // Clear editor for new quote
        editor.commands.clearContent()
      }
    }
  }, [editor, isViewMode, isCreateMode, existingQuote?.description])

  // Reset form when switching between create/view modes
  React.useEffect(() => {
    if (isViewMode && existingQuote) {
      form.reset({
        title: existingQuote.title || '',
        description: existingQuote.description || '',
        validUntil: existingQuote.validUntil
          ? new Date(
              typeof existingQuote.validUntil.seconds === 'bigint'
                ? Number(existingQuote.validUntil.seconds) * 1000
                : existingQuote.validUntil.seconds * 1000
            )
          : defaultValidUntil,
        // Initialize discount fields from existing quote - preserve all fields independently
        discountId: existingQuote?.discountId || undefined,
        discountName: existingQuote?.discountName || undefined,
        discountType: sanitizeDiscountType(existingQuote?.discountType),
        discountValue: existingQuote?.discountValue || undefined,
        enforcedTipEnabled: existingQuote.enforcedTipEnabled || false,
        enforcedTipPercentage: existingQuote.enforcedTipPercentage || undefined,
        tipAllocationMode: normalizeTipAllocationMode(existingQuote.tipAllocationMode),
        requiresQuestionnaire: existingQuote.requiresQuestionnaire ?? inquiry.requiresQuestionnaire ?? false,
        quoteEvents: existingQuote?.quoteEvents && existingQuote.quoteEvents.length > 0
          ? existingQuote.quoteEvents.map((qe) => ({
              tempId: crypto.randomUUID(),
              id: qe.id,
              eventDate: qe.eventDate ? timestampToDate(qe.eventDate) : undefined,
              eventLength: qe.eventLength,
              eventGuests: qe.eventGuests,
              eventTimezone: qe.eventTimezone,
              eventLocation: qe.eventLocation,
              baristaCount: qe.baristaCount,
              cartCount: qe.cartCount,
            } as QuoteEventFormValue))
          : [],
        items: initialItems.map(item => ({
          ...item,
          salesTaxRateId: item.salesTaxRateId ?? undefined,
          salesTaxRateName: item.salesTaxRateName ?? undefined,
          salesTaxRate: item.salesTaxRate ?? undefined,
        })),
        addonOffers: initialAddonOffers,
        paymentSchedules: existingQuote?.paymentSchedules
          ? paymentSchedulesToFormValues(existingQuote.paymentSchedules)
          : [],
        useCustomPaymentMethods: (existingQuote?.checkoutPaymentMethods?.length ?? 0) > 0,
        checkoutPaymentMethods: existingQuote?.checkoutPaymentMethods?.length
          ? [...existingQuote.checkoutPaymentMethods]
          : [...DEFAULT_CHECKOUT_PAYMENT_METHODS],
      })
    } else if (isCreateMode) {
      validUntilManuallyEditedRef.current = false
      requiresQuestionnaireManuallyEditedRef.current = false
      form.reset({
        title: '',
        description: '',
        validUntil: computeDefaultValidUntil(mapInquiryEventsToFormValues().map((event) => event.eventDate)),
        discountId: undefined,
        discountName: undefined,
        discountType: undefined,
        discountValue: undefined,
        enforcedTipEnabled: false,
        enforcedTipPercentage: undefined,
        tipAllocationMode: orgTipAllocationMode,
        requiresQuestionnaire: inquiry.requiresQuestionnaire ?? false,
        quoteEvents: mapInquiryEventsToFormValues(),
        items: [],
        addonOffers: [],
        paymentSchedules: [],
        useCustomPaymentMethods: false,
        checkoutPaymentMethods: [...DEFAULT_CHECKOUT_PAYMENT_METHODS],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isViewMode, isCreateMode, existingQuote?.id])

  // Apply quote prefill once per template/no-template selection in create mode.
  React.useEffect(() => {
    if (!isCreateMode || !hasInitializedTemplateSelection) return

    const selectionKey = selectedQuoteTemplateId || 'none'
    if (appliedTemplateSelectionKey === selectionKey) return
    if (selectedQuoteTemplateId ? preFillLoading : itemsLoading) return

    const nextItems = initialItems.map(item => ({
      ...item,
      salesTaxRateId: item.salesTaxRateId ?? undefined,
      salesTaxRateName: item.salesTaxRateName ?? undefined,
      salesTaxRate: item.salesTaxRate ?? undefined,
    }))

    const nextEvents = preFillQuoteEvents.length > 0
      ? mapPreFillQuoteEventsToFormValues()
      : mapInquiryEventsToFormValues()
    const nextTitle = selectedQuoteTemplateId ? preFillQuoteTitle || '' : ''
    const nextDescription = selectedQuoteTemplateId ? preFillQuoteDescription || '' : ''

    form.setFieldValue('title', nextTitle)
    form.setFieldValue('description', nextDescription)
    form.setFieldValue('items', nextItems)
    form.setFieldValue('addonOffers', initialAddonOffers)
    form.setFieldValue('quoteEvents', nextEvents)
    if (!validUntilManuallyEditedRef.current) {
      form.setFieldValue(
        'validUntil',
        computeDefaultValidUntil(nextEvents.map((event) => event.eventDate)),
      )
    }
    if (editor) {
      editor.commands.setContent(nextDescription)
    }
    setAppliedTemplateSelectionKey(selectionKey)
  }, [
    appliedTemplateSelectionKey,
    editor,
    form,
    hasInitializedTemplateSelection,
    initialAddonOffers,
    initialItems,
    isCreateMode,
    itemsLoading,
    mapInquiryEventsToFormValues,
    mapPreFillQuoteEventsToFormValues,
    preFillLoading,
    preFillQuoteDescription,
    preFillQuoteEvents.length,
    preFillQuoteTitle,
    selectedQuoteTemplateId,
  ])

  React.useEffect(() => {
    if (!isCreateMode || validUntilManuallyEditedRef.current) return
    form.setFieldValue(
      'validUntil',
      computeDefaultValidUntil(form.state.values.quoteEvents.map((event) => event.eventDate)),
    )
  }, [isCreateMode, computeDefaultValidUntil, form, orgQuoteExpirationRule])

  const quoteEventDatesKey = form.state.values.quoteEvents
    .map((event) => event.eventDate?.getTime() ?? '')
    .join('|')
  React.useEffect(() => {
    const eventDates = form.state.values.quoteEvents.map((event) => event.eventDate)
    const currentSchedules = form.state.values.paymentSchedules
    const nextSchedules = recomputeBeforeEventPreviewDueDates(currentSchedules, new Date(), eventDates)
    const changed = nextSchedules.some((schedule, index) => {
      const current = currentSchedules[index]
      return !current || current.dueDate.getTime() !== schedule.dueDate.getTime()
    })
    if (changed) {
      form.setFieldValue('paymentSchedules', nextSchedules)
      if (isEditing) setHasUnsavedChanges(true)
    }
  }, [form, quoteEventDatesKey, isEditing])

  // CLIENT-SIDE ENRICHMENT: Populate missing discount fields from organization's discount list
  // This handles legacy quotes where backend might not have enriched the data yet
  // Only runs ONCE on initial load when existingQuote.id changes - not on every form change
  const [hasEnrichedDiscount, setHasEnrichedDiscount] = React.useState(false)
  React.useEffect(() => {
    if (!isViewMode || !existingQuote || hasEnrichedDiscount) return

    const needsEnrichment =
      existingQuote.discountId &&
      !existingQuote.discountName && // Check existingQuote, not form state
      discounts.length > 0

    if (needsEnrichment) {
      const matchedDiscount = discounts.find((d) => d.id === existingQuote.discountId)

      if (matchedDiscount) {
        // Populate missing fields from organization's discount catalog
        form.setFieldValue('discountName', matchedDiscount.name)
        form.setFieldValue('discountType', sanitizeDiscountType(matchedDiscount.discountType))
        form.setFieldValue('discountValue', matchedDiscount.value)
      }
    }
    setHasEnrichedDiscount(true)
  }, [isViewMode, existingQuote?.id, existingQuote?.discountId, existingQuote?.discountName, discounts, form, hasEnrichedDiscount])

  // Pre-populate enforced tip from organization settings when creating new quote
  React.useEffect(() => {
    // Only for create mode, when we have an enforced tip, and form is at default
    if (
      isCreateMode &&
      hasEnforcedTip &&
      orgTipPercentage > 0 &&
      !form.state.values.enforcedTipEnabled &&
      form.state.values.enforcedTipPercentage === undefined
    ) {
      form.setFieldValue('enforcedTipEnabled', true)
      form.setFieldValue('enforcedTipPercentage', orgTipPercentage)
    }
  }, [isCreateMode, hasEnforcedTip, orgTipPercentage, form])

  // Seed the tip collection mode from the org default when creating, once the
  // organization has loaded, unless the user already picked one.
  React.useEffect(() => {
    if (!isCreateMode) return
    if (form.getFieldMeta('tipAllocationMode')?.isTouched) return
    if (form.state.values.tipAllocationMode !== orgTipAllocationMode) {
      form.setFieldValue('tipAllocationMode', orgTipAllocationMode)
    }
  }, [isCreateMode, orgTipAllocationMode, form])

  // Subscribe to form fields that affect money. `form.state.values` is not
  // reactive, so Summary/Preview stayed stale until Generate re-rendered.
  const {
    subtotalCents,
    discountAmountCents,
    discountRatio,
    taxAmountCents,
    enforcedTipCents,
    totalCents,
    items: liveItems,
    addonOffers: liveAddonOffers,
  } = useQuoteBuilderLiveTotals(form, {
    isReadOnly,
    existingTotals: isReadOnly && existingQuote
      ? {
          subtotalCents: Number(existingQuote.subtotalCents),
          discountCents: Number(existingQuote.discountCents),
          salesTaxCents: Number(existingQuote.salesTaxCents),
          enforcedTipCents: Number(existingQuote.enforcedTipCents || 0),
          totalCents: Number(existingQuote.totalCents),
        }
      : undefined,
  })

  const itemCount = liveItems.length
  // Lifted to component scope so the send dialog can enforce the same Stripe
  // gate as the trigger button. It used to live inside the button's
  // form.Subscribe, which is why only the builder had the gate at all.
  const hasPaidItems =
    liveItems.some((li) => (li.unitPriceCents ?? 0) > 0) ||
    liveAddonOffers.some((offer) => offer.unitPriceCents > 0 && offer.quantity > 0)
  const stripeBlocksSend = stripeHasLoaded && hasPaidItems && !isStripeConnected
  const isDisabled = isReadOnly || createMutation.isPending || updateMutation.isPending
  const isPending = createMutation.isPending || updateMutation.isPending
  // Update editor editable state when isDisabled changes
  React.useEffect(() => {
    if (editor) {
      editor.setEditable(!isDisabled)
    }
  }, [editor, isDisabled])

  const discardChanges = () => {
    setIsEditing(false)
    setHasUnsavedChanges(false)
    editor?.commands.setContent(existingQuote?.description || '')
    form.reset({
      title: existingQuote?.title || '',
      description: existingQuote?.description || '',
      validUntil: existingQuote?.validUntil
        ? new Date(
            typeof existingQuote.validUntil.seconds === 'bigint'
              ? Number(existingQuote.validUntil.seconds) * 1000
              : existingQuote.validUntil.seconds * 1000
          )
        : defaultValidUntil,
      discountId: existingQuote?.discountId || undefined,
      discountName: existingQuote?.discountName || undefined,
      discountType: sanitizeDiscountType(existingQuote?.discountType),
      discountValue: existingQuote?.discountValue || undefined,
      enforcedTipEnabled: existingQuote?.enforcedTipEnabled || false,
      enforcedTipPercentage: existingQuote?.enforcedTipPercentage || undefined,
      tipAllocationMode: existingQuote
        ? normalizeTipAllocationMode(existingQuote.tipAllocationMode)
        : orgTipAllocationMode,
      requiresQuestionnaire: existingQuote?.requiresQuestionnaire ?? inquiry.requiresQuestionnaire ?? false,
      quoteEvents:
        existingQuote?.quoteEvents && existingQuote.quoteEvents.length > 0
          ? existingQuote.quoteEvents.map((qe) =>
              ({
                tempId: crypto.randomUUID(),
                id: qe.id,
                eventDate: qe.eventDate ? timestampToDate(qe.eventDate) : undefined,
                eventLength: qe.eventLength,
                eventGuests: qe.eventGuests,
                eventTimezone: qe.eventTimezone,
                eventLocation: qe.eventLocation,
                baristaCount: qe.baristaCount,
                cartCount: qe.cartCount,
              }) as QuoteEventFormValue
            )
          : [],
      items: initialItems.map((item) => ({
        ...item,
        salesTaxRateId: item.salesTaxRateId ?? undefined,
        salesTaxRateName: item.salesTaxRateName ?? undefined,
        salesTaxRate: item.salesTaxRate ?? undefined,
      })),
      addonOffers: initialAddonOffers,
      paymentSchedules: existingQuote?.paymentSchedules
        ? paymentSchedulesToFormValues(existingQuote.paymentSchedules)
        : [],
      useCustomPaymentMethods: (existingQuote?.checkoutPaymentMethods?.length ?? 0) > 0,
      checkoutPaymentMethods: existingQuote?.checkoutPaymentMethods?.length
        ? [...existingQuote.checkoutPaymentMethods]
        : [...DEFAULT_CHECKOUT_PAYMENT_METHODS],
    })
  }

  const sendQuoteButton = canSend ? (() => {
    const button = (
      <Button
        type="button"
        className="w-full"
        onClick={handleOpenSendDialog}
        disabled={isPending || isSendingQuote || stripeBlocksSend}
      >
        {isSendingQuote ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Send className="size-4" />
        )}
        {isDraft ? 'Publish & Send Link' : 'Send Portal Link'}
      </Button>
    )

    if (!stripeBlocksSend) return button

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0}>{button}</span>
        </TooltipTrigger>
        <TooltipContent>Connect Stripe before publishing quotes with paid items.</TooltipContent>
      </Tooltip>
    )
  })() : null

  const railActions = (
    <>
      {isCreateMode && (
        <Button
          type="button"
          className="w-full"
          onClick={() => form.handleSubmit()}
          disabled={isPending}
        >
          <Send className="size-4" />
          {createMutation.isPending ? 'Creating…' : 'Create Quote'}
        </Button>
      )}
      {sendQuoteButton}
      {isViewMode && !isEditing && !isAccepted && (
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => setIsEditing(true)}
          disabled={isPending}
        >
          Edit Quote
        </Button>
      )}
      {isViewMode && isEditing && (
        <form.Subscribe selector={(state) => state.values.paymentSchedules}>
          {(paymentSchedules) => (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => form.handleSubmit()}
              disabled={
                isPending
                || itemCount === 0
                || !!getQuotePaymentScheduleValidationIssue(paymentSchedules, totalCents)
              }
            >
              {updateMutation.isPending ? 'Saving…' : 'Save Changes'}
            </Button>
          )}
        </form.Subscribe>
      )}
      {isViewMode && isAccepted && (
        <p className="text-sm text-muted-foreground">Accepted quotes cannot be edited.</p>
      )}
      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={() => {
          if (isEditing) {
            if (hasUnsavedChanges) {
              setDiscardDialogOpen(true)
              return
            }
            discardChanges()
          } else {
            onCancel()
          }
        }}
        disabled={isPending}
      >
        {isEditing ? 'Cancel' : isViewMode ? 'Back' : 'Cancel'}
      </Button>
    </>
  )

  // The Radix Selects nested below (e.g. DiscountSelector) compose a
  // useState setter into their ref, so tearing down and rebuilding this
  // subtree on every loading<->ready flip re-fires that setter each time —
  // React's update-depth limit can trip if that keeps happening (#185). Keep
  // the wrapper element type stable across both states so React updates the
  // subtree in place instead of remounting it.
  //
  // After CreateQuote, useCreateQuote already seeds getQuote. Gate on
  // hasLoaded (data in cache) rather than isLoading so create→view does not
  // swap a live DiscountSelector for the skeleton for one frame.
  const showQuoteBuilderSkeleton =
    (isViewMode && !quoteHasLoaded) || (isCreateMode && shouldPreFill && preFillLoading)

  if (showQuoteBuilderSkeleton) {
    return (
      <div className="space-y-4">
        <QuoteBuilderSkeleton />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {stripeNotConnected && (
        <Alert>
          <CreditCard className="h-4 w-4" />
          <AlertTitle>Connect Stripe to send paid quotes</AlertTitle>
          <AlertDescription>
            <p>Free quotes can still be drafted. Connect Stripe to set prices and send paid quotes to customers.</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleConnectStripe}
              disabled={getStripeAccountLink.isPending || !organizationId}
            >
              {getStripeAccountLink.isPending ? 'Connecting…' : 'Connect Stripe'}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,27rem)] xl:items-start">
        <div className="min-w-0 space-y-4">
          <form.Subscribe selector={(state) => ({
            title: state.values.title,
            validUntil: state.values.validUntil,
            description: state.values.description,
            requiresQuestionnaire: state.values.requiresQuestionnaire,
          })}>
            {({ title, validUntil, description, requiresQuestionnaire }) => (
              <QuoteBuilderDetailsCard
                sectionId={QUOTE_BUILDER_SECTION_DOM_IDS.details}
                statusBadge={quoteStatus !== undefined ? <QuoteStatusBadge status={quoteStatus} /> : undefined}
                recipientLabel={recipientLabel}
                templateSelect={isCreateMode && sortedActiveQuoteTemplates.length > 0 ? (
                  <Select
                    value={selectedQuoteTemplateId || 'none'}
                    onValueChange={(value) => {
                      setSelectedQuoteTemplateId(value === 'none' ? '' : value)
                    }}
                    disabled={isDisabled || quoteTemplatesLoading}
                  >
                    <SelectTrigger id="quote-template" aria-label="Quote template" className="w-[13rem]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No template</SelectItem>
                      {sortedActiveQuoteTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.title} - {getEventTypeLabel(template.eventTypeKey) || 'No event type'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : undefined}
                title={title}
                onTitleChange={(value) => {
                  form.setFieldValue('title', value)
                  if (isEditing) setHasUnsavedChanges(true)
                }}
                validUntil={validUntil}
                onValidUntilChange={(date) => {
                  validUntilManuallyEditedRef.current = true
                  form.setFieldValue('validUntil', date)
                  if (isEditing) setHasUnsavedChanges(true)
                }}
                descriptionEditor={
                  <RichTextEditor editor={editor} disabled={isDisabled} minHeight="100px" />
                }
                hasDescription={hasRichTextContent(description)}
                eventsSection={
                  <form.Field name="quoteEvents">
                    {(field) => (
                      <QuoteEventsSection
                        events={field.state.value}
                        onEventsChange={(events) => {
                          field.handleChange(events)
                          if (isCreateMode && !validUntilManuallyEditedRef.current) {
                            form.setFieldValue(
                              'validUntil',
                              computeDefaultValidUntil(events.map((event) => event.eventDate)),
                            )
                          }
                          if (isEditing) setHasUnsavedChanges(true)
                        }}
                        disabled={isDisabled}
                        inquiryEvents={inquiry.events}
                      />
                    )}
                  </form.Field>
                }
                requiresQuestionnaire={requiresQuestionnaire}
                onRequiresQuestionnaireChange={(checked) => {
                  requiresQuestionnaireManuallyEditedRef.current = true
                  form.setFieldValue('requiresQuestionnaire', checked)
                  if (isEditing) setHasUnsavedChanges(true)
                }}
                disabled={isDisabled}
              />
            )}
          </form.Subscribe>

          <form.Subscribe selector={(state) => ({
            items: state.values.items,
            addonOffers: state.values.addonOffers,
            quoteEvents: state.values.quoteEvents,
          })}>
            {({ items, addonOffers, quoteEvents }) => {
              const transportationLine = getTransportationLineItem(items)
              const transportationHint = transportationLine
                ? `${formatMiles(transportationLine.quantity)} mi × $${formatCurrency(transportationLine.unitPriceCents)} per mile`
                : transportationItem
                  ? `Auto-calculated at $${formatDollars(transportationItem.price)} per mile`
                  : 'Configure a mileage rate in Services'

              const handleItemsChange = (nextItems: QuoteLineItem[]) => {
                form.setFieldValue('items', nextItems.map((item) => ({
                  ...item,
                  salesTaxRateId: item.salesTaxRateId ?? undefined,
                  salesTaxRateName: item.salesTaxRateName ?? undefined,
                  salesTaxRate: item.salesTaxRate ?? undefined,
                  salesTaxCents: item.salesTaxCents ?? 0,
                })))
                setHasUnsavedChanges(true)
              }

              return (
                <QuoteBuilderLineItemsCard
                  sectionId={QUOTE_BUILDER_SECTION_DOM_IDS.items}
                  disabled={isDisabled}
                  itemsTable={itemsLoading || (isCreateMode && preFillLoading) ? (
                    <p className="py-8 text-center text-sm text-muted-foreground">Loading items…</p>
                  ) : (
                    <QuoteLineItemsTable
                      items={withoutTransportationLineItems(items)}
                      useStoredTotals={isReadOnly}
                      availableItems={availableItems}
                      availableTaxRates={taxRates}
                      presentation="rows"
                      onItemsChange={(nextItems) => {
                        handleItemsChange(replacePrimaryLineItems(items, nextItems))
                      }}
                      disabled={isDisabled}
                      quoteId={selectedQuoteId}
                      discountRatio={discountRatio}
                      discountAmountCents={discountAmountCents}
                      autoApplyTaxRateId={autoApplyTaxRateId}
                      onAutoApplyOverride={() => setAutoApplyTaxRateId('')}
                    />
                  )}
                  transportationHint={transportationHint}
                  transportationActive={
                    !!transportationLine || (isCreateMode && !!transportationItem?.alwaysInclude)
                  }
                  transportationSection={
                    <TransportationLineItemSection
                      embedded
                      documentKey={selectedQuoteId || `new-quote-${inquiry.id}`}
                      organizationId={inquiry.organizationId}
                      events={quoteEvents.map((event, index) => ({
                        key: event.id || event.tempId,
                        label: `Event ${index + 1} · ${formatEventDate(event.eventDate)}`,
                        location: event.eventLocation,
                      }))}
                      items={items}
                      taxRates={taxRates}
                      autoApplyTaxRateId={autoApplyTaxRateId}
                      disabled={isDisabled}
                      applyOrganizationDefault={isCreateMode}
                      onAutoApplyOverride={() => setAutoApplyTaxRateId('')}
                      onItemsChange={handleItemsChange}
                    />
                  }
                  addonCount={addonOffers.length}
                  addonsTable={
                    <QuoteAddonOffersTable
                      embedded
                      offers={addonOffers}
                      availableItems={availableItems}
                      availableTaxRates={taxRates}
                      quoteEvents={quoteEvents}
                      existingLineItemSourceIds={new Set(
                        items.map((item) => item.sourceItemId).filter((id): id is string => !!id)
                      )}
                      onOffersChange={(offers) => {
                        form.setFieldValue('addonOffers', offers)
                        if (isEditing) setHasUnsavedChanges(true)
                      }}
                      disabled={isDisabled}
                      autoApplyTaxRateId={autoApplyTaxRateId}
                      onAutoApplyOverride={() => setAutoApplyTaxRateId('')}
                    />
                  }
                  discountControl={
                    <form.Field name="discountId">
                      {(field) => {
                        const hasActualDiscountData =
                          form.state.values.discountName &&
                          form.state.values.discountValue !== undefined &&
                          form.state.values.discountValue > 0
                        const hasNoDiscountId = !field.state.value || field.state.value === ''
                        const inlineDiscount = hasNoDiscountId && hasActualDiscountData
                          ? {
                              name: form.state.values.discountName as string,
                              type: form.state.values.discountType || DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE,
                              value: form.state.values.discountValue as number,
                            }
                          : undefined

                        return (
                          <DiscountSelector
                            value={field.state.value}
                            discounts={discounts}
                            organizationId={inquiry.organizationId}
                            inlineDiscount={inlineDiscount}
                            onChange={(discountId) => {
                              field.handleChange(discountId === undefined ? '' : discountId)
                              if (isEditing) setHasUnsavedChanges(true)
                            }}
                            onInlineDiscountChange={(discount) => {
                              form.setFieldValue('discountName', discount?.name)
                              form.setFieldValue('discountType', sanitizeDiscountType(discount?.type))
                              form.setFieldValue('discountValue', discount?.value)
                              if (isEditing) setHasUnsavedChanges(true)
                            }}
                            disabled={isDisabled}
                            supportsInlineDiscounts
                          />
                        )
                      }}
                    </form.Field>
                  }
                  taxControl={
                    <QuickTaxSelector
                      items={items}
                      addonOffers={addonOffers}
                      taxRates={taxRates}
                      organizationId={inquiry.organizationId}
                      onItemsChange={handleItemsChange}
                      onAddonOffersChange={(offers) => {
                        form.setFieldValue('addonOffers', offers)
                        if (isEditing) setHasUnsavedChanges(true)
                      }}
                      value={autoApplyTaxRateId}
                      onValueChange={setAutoApplyTaxRateId}
                      disabled={isDisabled}
                    />
                  }
                  tipControl={
                    <div className="grid gap-4">
                    <form.Field name="enforcedTipPercentage">
                      {(field) => (
                        <EnforcedTipInput
                          value={field.state.value || 0}
                          onChange={(value) => {
                            field.handleChange(value)
                            form.setFieldValue('enforcedTipEnabled', value > 0)
                            if (isEditing) setHasUnsavedChanges(true)
                          }}
                          disabled={isDisabled}
                          organizationDefault={orgTipPercentage}
                          showOrganizationHint={hasEnforcedTip}
                        />
                      )}
                    </form.Field>
                    <form.Field name="tipAllocationMode">
                      {(field) => (
                        <TipAllocationModeSelect
                          value={field.state.value}
                          onChange={(value) => {
                            field.handleChange(value)
                            if (isEditing) setHasUnsavedChanges(true)
                          }}
                          disabled={isDisabled}
                        />
                      )}
                    </form.Field>
                    </div>
                  }
                />
              )
            }}
          </form.Subscribe>

          <form.Subscribe selector={(state) => ({
            paymentSchedules: state.values.paymentSchedules,
            useCustomPaymentMethods: state.values.useCustomPaymentMethods,
            checkoutPaymentMethods: state.values.checkoutPaymentMethods,
          })}>
            {({ paymentSchedules, useCustomPaymentMethods, checkoutPaymentMethods }) => {
              const currentPaymentSchedules = paymentSchedules ?? []
              const handleSchedulesChange = (schedules: PaymentScheduleFormValue[]) => {
                form.setFieldValue('paymentSchedules', schedules)
                if (isEditing) setHasUnsavedChanges(true)
              }

              const hasCard = checkoutPaymentMethods.includes(
                StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_CARD,
              )
              const hasACH = checkoutPaymentMethods.includes(
                StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_US_BANK_ACCOUNT,
              )
              const toggleMethod = (method: StripeCheckoutPaymentMethodEnum, checked: boolean) => {
                const current = form.state.values.checkoutPaymentMethods
                const next = checked
                  ? [...current, method]
                  : current.filter((candidate) => candidate !== method)
                if (next.length === 0) return
                form.setFieldValue('checkoutPaymentMethods', next)
                if (isEditing) setHasUnsavedChanges(true)
              }

              return (
                <QuoteBuilderPaymentCard
                  sectionId={QUOTE_BUILDER_SECTION_DOM_IDS.payment}
                  schedules={currentPaymentSchedules}
                  totalCents={totalCents}
                  onSchedulesChange={handleSchedulesChange}
                  onApplyBalanceToLastPayment={() => {
                    handleSchedulesChange(
                      rebalanceQuoteFixedPaymentSchedulesForTotal(currentPaymentSchedules, totalCents),
                    )
                  }}
                  onAddBalanceAsNewPayment={() => {
                    handleSchedulesChange(
                      appendQuoteFixedPaymentScheduleForTotal(currentPaymentSchedules, totalCents),
                    )
                  }}
                  disabled={isDisabled}
                  depositDueDays={orgDepositDueDays}
                  remainderDueDays={orgRemainderDueDays}
                  eventDates={form.state.values.quoteEvents.map((event) => event.eventDate)}
                  useCustomPaymentMethods={useCustomPaymentMethods}
                  onUseCustomPaymentMethodsChange={(checked) => {
                    form.setFieldValue('useCustomPaymentMethods', checked)
                    if (!checked) {
                      form.setFieldValue('checkoutPaymentMethods', [...DEFAULT_CHECKOUT_PAYMENT_METHODS])
                    }
                    if (isEditing) setHasUnsavedChanges(true)
                  }}
                  paymentMethodsControl={
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Label htmlFor="quote-payment-method-card" className="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-normal">
                        <Checkbox
                          id="quote-payment-method-card"
                          checked={hasCard}
                          onCheckedChange={(checked) =>
                            toggleMethod(StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_CARD, !!checked)
                          }
                          disabled={isDisabled || (hasCard && !hasACH)}
                        />
                        Credit Card
                      </Label>
                      <Label htmlFor="quote-payment-method-ach" className="flex min-h-11 cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm font-normal">
                        <Checkbox
                          id="quote-payment-method-ach"
                          checked={hasACH}
                          onCheckedChange={(checked) =>
                            toggleMethod(StripeCheckoutPaymentMethodEnum.STRIPE_CHECKOUT_PAYMENT_METHOD_US_BANK_ACCOUNT, !!checked)
                          }
                          disabled={isDisabled || (hasACH && !hasCard)}
                        />
                        ACH / Bank Transfer
                      </Label>
                    </div>
                  }
                />
              )
            }}
          </form.Subscribe>
        </div>

        <div className="min-w-0 xl:sticky xl:top-4 xl:max-h-[calc(100dvh-2rem)] xl:overflow-y-auto">
          <form.Subscribe selector={(state) => state.values}>
            {(values) => {
              const paymentScheduleIssue = getQuotePaymentScheduleValidationIssue(
                values.paymentSchedules,
                totalCents,
              )
              const checklist = getQuoteBuilderChecklist({
                title: values.title,
                itemCount: values.items.length,
                eventCount: values.quoteEvents.length,
                paymentScheduleIssueMessage: paymentScheduleIssue?.message,
                isStripeConnected: !stripeHasLoaded || isStripeConnected,
              })
              const scheduleRows = values.paymentSchedules.map((schedule, index) => ({
                key: `${schedule.sequenceNumber}-${index}`,
                label: `${schedule.description || `Payment ${index + 1}`} — ${formatQuotePaymentScheduleDue(schedule).toLowerCase()}`,
                amountCents: getQuotePaymentScheduleAmountCents(schedule, totalCents),
              }))
              const discountLabel = values.discountType === DiscountTypeEnum.DISCOUNT_TYPE_PERCENTAGE && values.discountValue
                ? `Discount (${values.discountValue}%)`
                : 'Discount'
              const eventsSummary = summarizeQuoteBuilderEvents(values.quoteEvents)

              return (
                <QuoteBuilderSummaryRail
                  totalCents={totalCents}
                  subtotalCents={subtotalCents}
                  discountAmountCents={discountAmountCents}
                  discountLabel={discountLabel}
                  taxAmountCents={taxAmountCents}
                  tipCents={enforcedTipCents}
                  tipLabel={values.enforcedTipPercentage
                    ? `Enforced tip (${values.enforcedTipPercentage}%)`
                    : undefined}
                  schedules={scheduleRows}
                  checklist={checklist}
                  onChecklistSelect={focusSection}
                  actions={railActions}
                  preview={
                    <QuoteBuilderPreview
                      recipientFirstName={quoteContact?.firstName?.trim() || recipientName}
                      title={values.title}
                      validUntilLabel={values.validUntil ? formatEventDate(values.validUntil) : undefined}
                      eventsSummary={eventsSummary.hasEvents
                        ? [eventsSummary.countLabel, eventsSummary.detail].filter(Boolean).join(' · ')
                        : undefined}
                      lineItems={values.items.map((item, index) => ({
                        key: item.id || item.tempId || `item-${index}`,
                        name: item.name,
                        amountCents: calculateLineTotalCents(item.quantity, item.unitPriceCents),
                        quantity: item.quantity,
                        unitPriceCents: item.unitPriceCents,
                        unit: item.unit,
                        salesTaxRate: item.salesTaxRate,
                        isTaxable: item.isTaxable,
                        salesTaxCents: item.salesTaxCents,
                      }))}
                      addonOffers={values.addonOffers.map((offer, index) => ({
                        key: offer.id || offer.tempId || `addon-${index}`,
                        name: offer.name,
                        amountCents: calculateLineTotalCents(offer.quantity, offer.unitPriceCents),
                        quantity: offer.quantity,
                        unitPriceCents: offer.unitPriceCents,
                        unit: offer.unit,
                        salesTaxRate: offer.salesTaxRate,
                        isTaxable: offer.isTaxable,
                      }))}
                      subtotalCents={subtotalCents}
                      discountAmountCents={discountAmountCents}
                      discountLabel={discountLabel}
                      taxAmountCents={taxAmountCents}
                      tipCents={enforcedTipCents}
                      tipLabel={values.enforcedTipPercentage
                        ? `Enforced tip (${values.enforcedTipPercentage}%)`
                        : undefined}
                      totalCents={totalCents}
                      schedules={scheduleRows}
                    />
                  }
                />
              )
            }}
          </form.Subscribe>
        </div>
      </div>

      <AlertDialog open={discardDialogOpen} onOpenChange={setDiscardDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard Unsaved Changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to discard them?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDiscardDialogOpen(false)
                discardChanges()
              }}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {selectedQuoteId && (
        <QuoteSendDialog
          key={selectedQuoteId}
          open={showQuoteSendDialog}
          onOpenChange={setShowQuoteSendDialog}
          organizationId={organizationId}
          quoteId={selectedQuoteId}
          isDraft={isDraft}
          recipientName={recipientName}
          recipientEmail={recipientEmail}
          isSending={isSendingQuote}
          stripeBlocksSend={stripeBlocksSend}
          onSend={handleSendQuote}
        />
      )}
    </div>
  )
}
